import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// MUI per-component imports (no barrel imports)
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Collapse from "@mui/material/Collapse";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import Skeleton from "@mui/material/Skeleton";
import Slider from "@mui/material/Slider";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { DataGrid } from "@mui/x-data-grid";

// MUI icons
import AssignmentTurnedInRoundedIcon from "@mui/icons-material/AssignmentTurnedInRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import QuizRoundedIcon from "@mui/icons-material/QuizRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

// App utilities
import { showErrorToast } from "../../../app/utils/helpers";
import Langs, { Localize } from "../../../app/lang/langs";
import ExcelManager from "../../../app/managers/excelManager";

// Redux thunks from vsecuriteSlice
import {
    fetchDashboardByTeam,
    fetchDashboardExport,
    fetchDashboardSummary,
} from "../vsecuriteSlice";

// Mobile-only dashboard layout
import VSecuriteDashboardMobile from "./VSecuriteDashboardMobile";

/** Primary brand colour used throughout this component */
const BRAND_ORANGE = "#FF9015";
const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

/** Colour used for alert/danger states (pending counts > 0, low rates) */
const ALERT_RED = "#EF4444";

/** Green threshold — completion/pass rate above this value */
const RATE_GREEN_THRESHOLD = 80;

/** Orange threshold — completion/pass rate above this value (but below green) */
const RATE_ORANGE_THRESHOLD = 50;

// ─── Utility helpers ──────────────────────────────────────────────────────────

/**
 * Returns the appropriate colour for a progress bar based on its rate value.
 * Green for >= 80%, orange for >= 50%, red otherwise.
 * @param {number} rate - Percentage value between 0 and 100.
 * @returns {string} CSS colour string.
 */
function getRateColor(rate) {
    if (rate >= RATE_GREEN_THRESHOLD) return "#22C55E";
    if (rate >= RATE_ORANGE_THRESHOLD) return BRAND_ORANGE;
    return ALERT_RED;
}

/**
 * Safely converts a value to a number, returning 0 for null/undefined.
 * @param {*} val - Value to coerce.
 * @returns {number}
 */
function toNum(val) {
    const n = Number(val);
    return isNaN(n) ? 0 : n;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/**
 * SummaryCard — one of the four KPI cards at the top of the dashboard.
 * Renders a MUI Card with a coloured left border, a main count, a subtitle,
 * and an optional rate badge beneath it.
 */
function SummaryCard({ icon, title, value, rateLabel, borderColor, isAlert, isMobileScreen }) {
    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: "16px",
                border: "1px solid rgba(0,40,128,0.06)",
                borderLeft: `4px solid ${borderColor}`,
                background: "rgba(255,255,255,0.75)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                height: "100%",
                transition: `all 300ms ${EASE}`,
                boxShadow: `0 1px 4px rgba(255,144,21,0.04), 0 4px 16px rgba(0,40,128,0.03)`,
                "&:hover": {
                    boxShadow: `0 8px 32px rgba(0,40,128,0.08), 0 2px 8px rgba(255,144,21,0.08)`,
                    transform: isMobileScreen ? "none" : "translateY(-2px)",
                },
            }}
        >
            <CardContent sx={{ p: isMobileScreen ? 2 : 2.5, "&:last-child": { pb: isMobileScreen ? 2 : 2.5 } }}>
                {/* Title row with icon */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 38,
                            height: 38,
                            borderRadius: "11px",
                            bgcolor: `${borderColor}14`,
                            transition: `background-color 250ms ${EASE}`,
                        }}
                    >
                        {React.cloneElement(icon, {
                            sx: { fontSize: 20, color: borderColor },
                        })}
                    </Box>
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 500,
                            color: "rgba(30,30,30,0.7)",
                            lineHeight: 1.3,
                            fontSize: "0.82rem",
                            letterSpacing: "-0.01em",
                        }}
                    >
                        {title}
                    </Typography>
                </Box>

                {/* Main metric */}
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        fontSize: isMobileScreen ? "1.6rem" : "2rem",
                        color: isAlert ? ALERT_RED : "rgba(15,15,15,0.88)",
                        lineHeight: 1,
                        mb: rateLabel ? 1 : 0,
                        letterSpacing: "-0.03em",
                    }}
                >
                    {value}
                </Typography>

                {/* Optional rate subtitle */}
                {rateLabel && (
                    <Typography
                        variant="caption"
                        sx={{
                            display: "block",
                            color: "rgba(100,116,139,0.8)",
                            fontSize: "0.75rem",
                            mt: 0.5,
                        }}
                    >
                        {rateLabel}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
}

/**
 * SkeletonCard — placeholder card shown while dashboard data is loading.
 */
function SkeletonCard() {
    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: "16px",
                border: "1px solid rgba(0,40,128,0.06)",
                borderLeft: `4px solid rgba(0,0,0,0.08)`,
                background: "rgba(255,255,255,0.75)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                height: "100%",
            }}
        >
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <Skeleton variant="rounded" width={36} height={36} />
                    <Skeleton variant="text" width="60%" height={16} />
                </Box>
                <Skeleton variant="text" width="30%" height={40} />
                <Skeleton variant="text" width="70%" height={14} sx={{ mt: 0.5 }} />
            </CardContent>
        </Card>
    );
}

/**
 * RateProgressBar — renders a coloured LinearProgress inside a DataTable cell.
 * @param {number} rate - Completion/pass rate as a percentage (0–100).
 */
function RateProgressBar({ rate }) {
    const color = getRateColor(toNum(rate));

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 100, width: "100%" }}>
            <Box sx={{ flex: 1 }}>
                <LinearProgress
                    variant="determinate"
                    value={toNum(rate)}
                    sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: "rgba(0,40,128,0.06)",
                        "& .MuiLinearProgress-bar": {
                            borderRadius: 4,
                            bgcolor: color,
                            transition: `transform 600ms ${EASE}`,
                        },
                    }}
                />
            </Box>
            <Typography
                variant="caption"
                sx={{
                    fontWeight: 600,
                    fontSize: "0.78rem",
                    color,
                    minWidth: 36,
                    textAlign: "right",
                }}
            >
                {toNum(rate)}%
            </Typography>
        </Box>
    );
}

/**
 * MobileTeamCard — card-based row used on mobile instead of DataGrid.
 * Shows team name, assigned/completed counts, and a rate bar.
 */
function MobileTeamCard({ row, totalField, completedField, rateField, totalLabel, completedLabel, rateLabel }) {
    const rate = toNum(row[rateField]);
    const color = getRateColor(rate);

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: "14px",
                border: "1px solid rgba(0,40,128,0.06)",
                background: "rgba(255,255,255,0.8)",
                mb: 1.5,
                transition: `all 200ms ${EASE}`,
                "&:active": { transform: "scale(0.99)" },
            }}
        >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                {/* Team name */}
                <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#1a1a2e", mb: 1.5 }}>
                    {row.team_name}
                </Typography>

                {/* Stats row */}
                <Box sx={{ display: "flex", gap: 2, mb: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ color: "rgba(100,116,139,0.7)", fontSize: "0.72rem", display: "block", mb: 0.25 }}>
                            {totalLabel}
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: "rgba(51,65,85,0.85)" }}>
                            {toNum(row[totalField])}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ color: "rgba(100,116,139,0.7)", fontSize: "0.72rem", display: "block", mb: 0.25 }}>
                            {completedLabel}
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: "rgb(22,163,74)" }}>
                            {toNum(row[completedField])}
                        </Typography>
                    </Box>
                </Box>

                {/* Rate bar */}
                <Box>
                    <Typography variant="caption" sx={{ color: "rgba(100,116,139,0.7)", fontSize: "0.72rem", display: "block", mb: 0.5 }}>
                        {rateLabel}
                    </Typography>
                    <RateProgressBar rate={rate} />
                </Box>
            </CardContent>
        </Card>
    );
}

/**
 * EmptyState — renders a centred empty-data message when byTeam is empty.
 */
function EmptyState({ message }) {
    return (
        <Box
            sx={{
                py: 6,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
            }}
        >
            <SecurityRoundedIcon
                sx={{ fontSize: 48, color: "rgba(0,0,0,0.12)" }}
            />
            <Typography
                variant="body2"
                sx={{
                    color: "rgba(0,0,0,0.35)",
                    fontStyle: "italic",
                    fontSize: "0.9rem",
                }}
            >
                {message}
            </Typography>
        </Box>
    );
}

/**
 * SkeletonTable — placeholder table rows shown while byTeam data loads.
 */
function SkeletonTable() {
    return (
        <Box sx={{ mt: 1 }}>
            {Array.from({ length: 5 }).map((_, i) => (
                <Box
                    key={i}
                    sx={{
                        display: "flex",
                        gap: 2,
                        py: 1.5,
                        borderBottom: "1px solid rgba(0,0,0,0.05)",
                        alignItems: "center",
                    }}
                >
                    <Skeleton variant="text" width="20%" height={18} />
                    <Skeleton variant="text" width="12%" height={18} />
                    <Skeleton variant="text" width="12%" height={18} />
                    <Skeleton variant="rounded" width="40%" height={10} />
                </Box>
            ))}
        </Box>
    );
}

/**
 * MobileSkeletonCards — placeholder cards shown on mobile while data loads.
 */
function MobileSkeletonCards() {
    return (
        <Box>
            {Array.from({ length: 3 }).map((_, i) => (
                <Card
                    key={i}
                    elevation={0}
                    sx={{
                        borderRadius: "14px",
                        border: "1px solid rgba(0,40,128,0.06)",
                        background: "rgba(255,255,255,0.8)",
                        mb: 1.5,
                    }}
                >
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                        <Skeleton variant="text" width="50%" height={20} sx={{ mb: 1 }} />
                        <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
                            <Skeleton variant="text" width="30%" height={28} />
                            <Skeleton variant="text" width="30%" height={28} />
                        </Box>
                        <Skeleton variant="rounded" width="100%" height={8} />
                    </CardContent>
                </Card>
            ))}
        </Box>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * VSecuriteDashboard — Admin overview dashboard for the V-Sécurité module.
 *
 * Displays four KPI summary cards followed by a tabbed breakdown table
 * (sensibilisation / quiz) broken down by team.
 * On mobile, the DataGrid is replaced with a card-based list layout.
 *
 * Data is fetched on mount via Redux thunks.
 */
export default function VSecuriteDashboard() {
    const dispatch = useDispatch();

    const isMobile = useSelector((state) => state.settings.isMobile);
    const isMobileScreen = isMobile === 2;

    /** Active tab index: 0 = Sensibilisation, 1 = Quiz */
    const [activeTab, setActiveTab] = useState(0);

    /** Whether an export request is currently in-flight */
    const [exporting, setExporting] = useState(false);

    /** Rate range filter [min, max] for the Sensibilisation tab (0–100) */
    const [awarenessRateRange, setAwarenessRateRange] = useState([0, 100]);

    /** Rate range filter [min, max] for the Quiz tab (0–100) */
    const [quizRateRange, setQuizRateRange] = useState([0, 100]);

    /** Whether the filter slider is expanded on mobile */
    const [filterExpanded, setFilterExpanded] = useState(false);

    // ── Redux selectors ──────────────────────────────────────────────────────
    const summary = useSelector((state) => state.vsecurite.dashboard.summary);
    const byTeam = useSelector((state) => state.vsecurite.dashboard.byTeam);
    const loading = useSelector((state) => state.vsecurite.dashboard.loading);
    const lang = useSelector((state) => state.settings.lang);

    // ── On mount: fetch both summary and byTeam data ─────────────────────────
    useEffect(() => {
        /**
         * Dispatches both dashboard data fetches simultaneously on component mount.
         * Errors are surfaced via showErrorToast.
         */
        async function loadDashboard() {
            try {
                await Promise.all([
                    dispatch(fetchDashboardSummary()),
                    dispatch(fetchDashboardByTeam()),
                ]);
            } catch {
                showErrorToast(Localize("VSEC_TOAST_DASHBOARD_LOAD_ERROR", lang));
            }
        }

        loadDashboard();
    }, [dispatch]);

    // ── Handlers ─────────────────────────────────────────────────────────────

    /**
     * Handles tab change events from the MUI Tabs component.
     */
    function handleTabChange(_event, newValue) {
        setActiveTab(newValue);
        setFilterExpanded(false);
    }

    /**
     * Fetches the full dashboard dataset from the export endpoint then builds
     * the Excel report (summary + per-team + per-collab × campaign +
     * per-collab × quiz). All sheets are sourced from the API response; the
     * Redux summary/byTeam are only used as a fallback if the endpoint omits
     * them (e.g. older backend).
     */
    async function handleExport() {
        setExporting(true);
        try {
            const result = await dispatch(fetchDashboardExport());
            const payload = result?.payload?.responseData || {};
            await ExcelManager.getInstance().generateAndDownloadVSecuriteDashboardReport(
                payload.summary || summary,
                payload.byTeam || byTeam,
                payload.awarenessDetails || [],
                payload.quizDetails || [],
                lang
            );
        } catch {
            showErrorToast(Localize("VSEC_TOAST_DASHBOARD_EXPORT_ERROR", lang));
        } finally {
            setExporting(false);
        }
    }

    // ── Derived values ────────────────────────────────────────────────────────

    const s = {
        campaigns: toNum(summary?.total_campaigns_published),
        campaignsRate: toNum(summary?.total_campaigns_completed_rate),
        quizzes: toNum(summary?.total_quizzes_published),
        quizzesRate: toNum(summary?.total_quizzes_passed_rate),
        pendingAwareness: toNum(summary?.employees_with_pending_awareness),
        pendingQuizzes: toNum(summary?.employees_with_pending_quizzes),
    };

    const hasTeamData = Array.isArray(byTeam) && byTeam.length > 0;

    const filteredAwarenessRows = Array.isArray(byTeam)
        ? byTeam.filter((r) => {
              const rate = toNum(r.awareness_rate);
              return rate >= awarenessRateRange[0] && rate <= awarenessRateRange[1];
          })
        : [];

    const filteredQuizRows = Array.isArray(byTeam)
        ? byTeam.filter((r) => {
              const rate = toNum(r.quizzes_rate);
              return rate >= quizRateRange[0] && rate <= quizRateRange[1];
          })
        : [];

    // ── DataGrid column definitions (desktop only) ────────────────────────────

    const awarenessColumns = [
        {
            field: "team_name",
            headerName: Localize("VSEC_COL_TEAM", lang),
            flex: 1.6,
            minWidth: 160,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 500 }}>{params.row.team_name}</span>
            ),
        },
        {
            field: "awareness_total",
            headerName: Localize("VSEC_COL_ASSIGNED_AWARENESS", lang),
            flex: 1,
            minWidth: 110,
            sortable: true,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 600, color: "rgba(51,65,85,0.85)" }}>
                    {toNum(params.row.awareness_total)}
                </Typography>
            ),
        },
        {
            field: "awareness_completed",
            headerName: Localize("VSEC_COL_COMPLETED", lang),
            flex: 1,
            minWidth: 100,
            sortable: true,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 600, color: "rgb(22,163,74)" }}>
                    {toNum(params.row.awareness_completed)}
                </Typography>
            ),
        },
        {
            field: "awareness_rate",
            headerName: Localize("VSEC_COL_AWARENESS_RATE", lang),
            flex: 2,
            minWidth: 200,
            sortable: true,
            renderCell: (params) => (
                <RateProgressBar rate={toNum(params.row.awareness_rate)} />
            ),
        },
    ];

    const quizColumns = [
        {
            field: "team_name",
            headerName: Localize("VSEC_COL_TEAM", lang),
            flex: 1.6,
            minWidth: 160,
            sortable: true,
            renderCell: (params) => (
                <span style={{ fontWeight: 500 }}>{params.row.team_name}</span>
            ),
        },
        {
            field: "quizzes_total",
            headerName: Localize("VSEC_COL_ASSIGNED_QUIZZES", lang),
            flex: 1,
            minWidth: 110,
            sortable: true,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 600, color: "rgba(51,65,85,0.85)" }}>
                    {toNum(params.row.quizzes_total)}
                </Typography>
            ),
        },
        {
            field: "quizzes_passed",
            headerName: Localize("VSEC_COL_PASSED", lang),
            flex: 1,
            minWidth: 100,
            sortable: true,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 600, color: "rgb(22,163,74)" }}>
                    {toNum(params.row.quizzes_passed)}
                </Typography>
            ),
        },
        {
            field: "quizzes_rate",
            headerName: Localize("VSEC_COL_QUIZ_RATE", lang),
            flex: 2,
            minWidth: 200,
            sortable: true,
            renderCell: (params) => (
                <RateProgressBar rate={toNum(params.row.quizzes_rate)} />
            ),
        },
    ];

    // ── Shared Slider styles ──────────────────────────────────────────────────

    const sliderSx = {
        color: BRAND_ORANGE,
        "& .MuiSlider-thumb": {
            width: isMobileScreen ? 22 : 16,
            height: isMobileScreen ? 22 : 16,
            transition: `box-shadow 200ms ${EASE}`,
            "&:hover, &.Mui-focusVisible": {
                boxShadow: "0 0 0 6px rgba(255,144,21,0.16)",
            },
        },
        "& .MuiSlider-valueLabel": {
            bgcolor: BRAND_ORANGE,
            borderRadius: "8px",
        },
        "& .MuiSlider-rail": {
            bgcolor: "rgba(0,40,128,0.08)",
        },
    };

    // ── DataGrid shared sx ────────────────────────────────────────────────────

    const dataGridSx = {
        height: "calc(100vh - 620px)",
        minHeight: 300,
        fontSize: "0.875rem",
        borderRadius: "14px",
        border: "1px solid rgba(0,40,128,0.06)",
        "& .MuiDataGrid-columnHeaders": {
            bgcolor: "rgba(247,248,252,0.9)",
            fontWeight: 600,
        },
        "& .MuiDataGrid-row": {
            transition: `background-color 200ms ${EASE}`,
        },
        "& .MuiDataGrid-row:hover": {
            bgcolor: "rgba(255,144,21,0.05)",
        },
        "& .MuiDataGrid-cell": {
            borderBottom: "1px solid rgba(0,40,128,0.04)",
        },
    };

    // ── Helper: render filter slider section ──────────────────────────────────

    function renderFilterSlider(rateRange, setRateRange, label) {
        const sliderContent = (
            <Box sx={{ px: 1, pb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: "rgba(100,116,139,0.9)", mb: 0.5, display: "block" }}>
                    {label}: {rateRange[0]}% — {rateRange[1]}%
                </Typography>
                <Slider
                    value={rateRange}
                    onChange={(_, val) => setRateRange(val)}
                    min={0}
                    max={100}
                    step={5}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `${v}%`}
                    sx={sliderSx}
                />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="caption" sx={{ color: "rgba(100,116,139,0.7)" }}>≥ {rateRange[0]}%</Typography>
                    <Typography variant="caption" sx={{ color: "rgba(100,116,139,0.7)" }}>≤ {rateRange[1]}%</Typography>
                </Box>
            </Box>
        );

        if (isMobileScreen) {
            return (
                <>
                    <Button
                        size="small"
                        startIcon={<FilterAltRoundedIcon />}
                        endIcon={<ExpandMoreRoundedIcon sx={{ transform: filterExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: `transform 200ms ${EASE}` }} />}
                        onClick={() => setFilterExpanded(!filterExpanded)}
                        sx={{
                            color: BRAND_ORANGE,
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.82rem",
                            mb: 1,
                            minHeight: 44,
                        }}
                    >
                        {Localize("VSEC_BTN_FILTER", lang) || "Filtrer"}
                    </Button>
                    <Collapse in={filterExpanded}>
                        {sliderContent}
                    </Collapse>
                </>
            );
        }

        return sliderContent;
    }

    // ── Render ────────────────────────────────────────────────────────────────

    if (isMobileScreen) {
        return (
            <VSecuriteDashboardMobile
                summary={summary}
                byTeam={byTeam}
                loading={loading}
                lang={lang}
                exporting={exporting}
                onExport={handleExport}
            />
        );
    }

    return (
        <Box
            sx={{
                p: { xs: 1.5, md: 3 },
                mt: 0,
                minHeight: "100%",
                "@keyframes dashFadeIn": {
                    from: { opacity: 0, transform: "translateY(10px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                },
                animation: `dashFadeIn 450ms ${EASE} both`,
            }}
        >
            {/* ── Page header ─────────────────────────────────────────────── */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: isMobileScreen ? 1.5 : 2,
                    mb: 3,
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            fontSize: isMobileScreen ? "1.15rem" : "1.35rem",
                            color: "rgba(15,15,15,0.88)",
                            letterSpacing: "-0.03em",
                        }}
                    >
                        <Langs str="VSEC_DASHBOARD_TITLE" />
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: "rgba(100,116,139,0.8)", fontSize: "0.82rem", mt: 0.25 }}
                    >
                        <Langs str="VSEC_DASHBOARD_SUBTITLE" />
                    </Typography>
                </Box>

                {/* Export button — full-width on mobile */}
                <Button
                    variant="contained"
                    startIcon={<DownloadRoundedIcon />}
                    onClick={handleExport}
                    disabled={exporting || loading}
                    fullWidth={isMobileScreen}
                    sx={{
                        bgcolor: BRAND_ORANGE,
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        borderRadius: "12px",
                        px: 2.5,
                        py: 1,
                        minHeight: 48,
                        textTransform: "none",
                        boxShadow: "0 2px 8px rgba(255,144,21,0.25)",
                        transition: `all 250ms ${EASE}`,
                        "&:hover": {
                            bgcolor: "#e07d0c",
                            boxShadow: "0 6px 20px rgba(255,144,21,0.35)",
                            transform: isMobileScreen ? "none" : "translateY(-1px) scale(1.02)",
                        },
                        "&:active": {
                            transform: "scale(0.98)",
                        },
                        "&.Mui-disabled": {
                            bgcolor: "rgba(255,144,21,0.4)",
                            color: "#fff",
                        },
                    }}
                >
                    {exporting ? Localize("VSEC_BTN_EXPORTING", lang) : Localize("VSEC_BTN_EXPORT_ALL", lang)}
                </Button>
            </Box>

            {/* ── Stats cards row ──────────────────────────────────────────── */}
            <Grid container spacing={isMobileScreen ? 1.5 : 2} sx={{ mb: 3 }}>
                {loading && !summary ? (
                    Array.from({ length: 4 }).map((_, i) => (
                        <Grid item xs={6} sm={6} lg={3} key={i}>
                            <SkeletonCard />
                        </Grid>
                    ))
                ) : (
                    <>
                        {/* Card 1 — Campagnes publiées */}
                        <Grid item xs={6} sm={6} lg={3}>
                            <SummaryCard
                                icon={<AssignmentTurnedInRoundedIcon />}
                                title={Localize("VSEC_CARD_CAMPAIGNS_PUBLISHED", lang)}
                                value={s.campaigns}
                                rateLabel={`${Localize("VSEC_CARD_RATE_COMPLETION", lang)} ${s.campaignsRate}%`}
                                borderColor={BRAND_ORANGE}
                                isMobileScreen={isMobileScreen}
                            />
                        </Grid>

                        {/* Card 2 — Quiz publiés */}
                        <Grid item xs={6} sm={6} lg={3}>
                            <SummaryCard
                                icon={<QuizRoundedIcon />}
                                title={Localize("VSEC_CARD_QUIZZES_PUBLISHED", lang)}
                                value={s.quizzes}
                                rateLabel={`${Localize("VSEC_CARD_RATE_SUCCESS", lang)} ${s.quizzesRate}%`}
                                borderColor={BRAND_ORANGE}
                                isMobileScreen={isMobileScreen}
                            />
                        </Grid>

                        {/* Card 3 — Collabs en attente (sensibilisation) */}
                        <Grid item xs={6} sm={6} lg={3}>
                            <SummaryCard
                                icon={<HourglassEmptyRoundedIcon />}
                                title={Localize("VSEC_CARD_PENDING_AWARENESS", lang)}
                                value={s.pendingAwareness}
                                borderColor={s.pendingAwareness > 0 ? ALERT_RED : BRAND_ORANGE}
                                isAlert={s.pendingAwareness > 0}
                                isMobileScreen={isMobileScreen}
                            />
                        </Grid>

                        {/* Card 4 — Collabs en attente (quiz) */}
                        <Grid item xs={6} sm={6} lg={3}>
                            <SummaryCard
                                icon={<WarningAmberRoundedIcon />}
                                title={Localize("VSEC_CARD_PENDING_QUIZZES", lang)}
                                value={s.pendingQuizzes}
                                borderColor={s.pendingQuizzes > 0 ? ALERT_RED : BRAND_ORANGE}
                                isAlert={s.pendingQuizzes > 0}
                                isMobileScreen={isMobileScreen}
                            />
                        </Grid>
                    </>
                )}
            </Grid>

            {/* ── Tabs + table section ─────────────────────────────────────── */}
            <Card
                elevation={0}
                sx={{
                    borderRadius: isMobileScreen ? "14px" : "18px",
                    border: "1px solid rgba(0,40,128,0.06)",
                    background: "rgba(255,255,255,0.8)",
                    backdropFilter: "blur(14px)",
                    WebkitBackdropFilter: "blur(14px)",
                    overflow: "hidden",
                    boxShadow: "0 1px 4px rgba(255,144,21,0.04), 0 8px 32px rgba(0,40,128,0.04)",
                    transition: `box-shadow 300ms ${EASE}`,
                }}
            >
                {/* Tab strip */}
                <Box
                    sx={{
                        borderBottom: "1px solid rgba(0,40,128,0.06)",
                        px: isMobileScreen ? 1 : 2,
                    }}
                >
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant={isMobileScreen ? "fullWidth" : "standard"}
                        sx={{
                            "& .MuiTab-root": {
                                fontWeight: 500,
                                fontSize: isMobileScreen ? "0.82rem" : "0.875rem",
                                textTransform: "none",
                                color: "rgba(100,116,139,0.7)",
                                minHeight: 48,
                                transition: `color 250ms ${EASE}`,
                                "&:hover": { color: "rgba(100,116,139,0.95)" },
                                "&.Mui-selected": {
                                    color: BRAND_ORANGE,
                                    fontWeight: 700,
                                },
                            },
                            "& .MuiTabs-indicator": {
                                bgcolor: BRAND_ORANGE,
                                height: 3,
                                borderRadius: "3px 3px 0 0",
                                boxShadow: "0 0 8px rgba(255,144,21,0.3)",
                            },
                        }}
                    >
                        <Tab label={Localize("VSEC_TAB_AWARENESS", lang)} />
                        <Tab label={Localize("VSEC_TAB_QUIZ", lang)} />
                    </Tabs>
                </Box>

                {/* Table panel */}
                <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
                    {loading && !hasTeamData ? (
                        isMobileScreen ? <MobileSkeletonCards /> : <SkeletonTable />
                    ) : !hasTeamData ? (
                        <EmptyState message={Localize("VSEC_EMPTY_TEAM_TABLE", lang)} />
                    ) : (
                        <>
                            {/* ── Tab panel: Sensibilisation ───────────────── */}
                            {activeTab === 0 && (
                                <>
                                    {renderFilterSlider(awarenessRateRange, setAwarenessRateRange, Localize("VSEC_COL_AWARENESS_RATE", lang))}

                                    {isMobileScreen ? (
                                        /* Mobile: card-based list */
                                        <Box sx={{
                                            maxHeight: "calc(100vh - 480px)",
                                            overflowY: "auto",
                                            WebkitOverflowScrolling: "touch",
                                            pb: 1,
                                        }}>
                                            {filteredAwarenessRows.length === 0 ? (
                                                <EmptyState message={Localize("VSEC_EMPTY_TEAM_TABLE", lang)} />
                                            ) : (
                                                filteredAwarenessRows.map((row) => (
                                                    <MobileTeamCard
                                                        key={row.team_name}
                                                        row={row}
                                                        totalField="awareness_total"
                                                        completedField="awareness_completed"
                                                        rateField="awareness_rate"
                                                        totalLabel={Localize("VSEC_COL_ASSIGNED_AWARENESS", lang)}
                                                        completedLabel={Localize("VSEC_COL_COMPLETED", lang)}
                                                        rateLabel={Localize("VSEC_COL_AWARENESS_RATE", lang)}
                                                    />
                                                ))
                                            )}
                                        </Box>
                                    ) : (
                                        /* Desktop: DataGrid */
                                        <DataGrid
                                            rows={filteredAwarenessRows}
                                            columns={awarenessColumns}
                                            getRowId={(row) => row.team_name}
                                            disableRowSelectionOnClick
                                            initialState={{
                                                pagination: {
                                                    paginationModel: { pageSize: 20 },
                                                },
                                            }}
                                            pageSizeOptions={[10, 20, 50]}
                                            density="compact"
                                            sx={dataGridSx}
                                        />
                                    )}
                                </>
                            )}

                            {/* ── Tab panel: Quiz ──────────────────────────── */}
                            {activeTab === 1 && (
                                <>
                                    {renderFilterSlider(quizRateRange, setQuizRateRange, Localize("VSEC_COL_QUIZ_RATE", lang))}

                                    {isMobileScreen ? (
                                        /* Mobile: card-based list */
                                        <Box sx={{
                                            maxHeight: "calc(100vh - 480px)",
                                            overflowY: "auto",
                                            WebkitOverflowScrolling: "touch",
                                            pb: 1,
                                        }}>
                                            {filteredQuizRows.length === 0 ? (
                                                <EmptyState message={Localize("VSEC_EMPTY_TEAM_TABLE", lang)} />
                                            ) : (
                                                filteredQuizRows.map((row) => (
                                                    <MobileTeamCard
                                                        key={row.team_name}
                                                        row={row}
                                                        totalField="quizzes_total"
                                                        completedField="quizzes_passed"
                                                        rateField="quizzes_rate"
                                                        totalLabel={Localize("VSEC_COL_ASSIGNED_QUIZZES", lang)}
                                                        completedLabel={Localize("VSEC_COL_PASSED", lang)}
                                                        rateLabel={Localize("VSEC_COL_QUIZ_RATE", lang)}
                                                    />
                                                ))
                                            )}
                                        </Box>
                                    ) : (
                                        /* Desktop: DataGrid */
                                        <DataGrid
                                            rows={filteredQuizRows}
                                            columns={quizColumns}
                                            getRowId={(row) => row.team_name}
                                            disableRowSelectionOnClick
                                            initialState={{
                                                pagination: {
                                                    paginationModel: { pageSize: 20 },
                                                },
                                            }}
                                            pageSizeOptions={[10, 20, 50]}
                                            density="compact"
                                            sx={dataGridSx}
                                        />
                                    )}
                                </>
                            )}
                        </>
                    )}
                </Box>
            </Card>
        </Box>
    );
}
